/**
 * useV2BootstrapData
 *
 * Loads the minimum reference data the V2 routes need IF the legacy
 * Dashboard hasn't already populated it.  Without this hook, opening
 * `/v2/calendar` directly (e.g., a bookmark, a deep-link) renders an
 * empty "All Calendars" dropdown because the static-data fetcher
 * lives inside the legacy `Dashboard` component (App.js:10879) and
 * therefore never runs when V2 is the only mounted tree.
 *
 * Scope: the bare minimum to make every V2 surface feel populated —
 * `providers` (the calendar dropdown), `locations`, `clients` /
 * `allClientsForLookup` (chip names + filters), `cptCodes` and
 * `cptBundles` (chip colours). Adding more endpoints here is fine;
 * just keep it lean — the legacy `fetchStaticData` still owns the
 * full set when Dashboard is mounted.
 */

import { useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStaticData } from '../context/StaticDataContext';
import { useClients } from '../context/ClientsContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const useV2BootstrapData = () => {
  const { user } = useAuth();
  const {
    providers, setProviders,
    locations, setLocations,
    cptCodes, setCptCodes,
    cptBundles, setCptBundles,
    serviceTypes, setServiceTypes,
    users, setUsers,
    roles, setRoles,
    posCodes, setPosCodes,
    organizations, setOrganizations,
    intakeBundles, setIntakeBundles,
    setStaticDataLoaded,
  } = useStaticData();
  const {
    allClientsForLookup, setAllClientsForLookup,
    clients, setClients,
    clientLabels, setClientLabels,
    clientGroups, setClientGroups,
    ensureClientsLoaded, ensureClientLabelsLoaded,
  } = useClients();

  useEffect(() => {
    if (!user?.id) return;

    // Each fetch is independent and only fires when its slice is
    // empty so we don't double-fetch when Dashboard has already
    // populated StaticDataContext.  Errors are silenced because the
    // legacy Dashboard fetcher will retry on its own paths.
    const safeFetch = async (url, setter, onSuccess) => {
      try {
        const { data } = await axios.get(`${API}${url}`);
        // Some endpoints (e.g., /api/clients) return `{ clients: [...] }`
        // while others return a bare array. Unwrap the common shapes.
        let list = data;
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list = list.clients || list.items || list.results || list.data || [];
        }
        setter(Array.isArray(list) ? list : []);
        if (onSuccess) onSuccess();
      } catch (_err) {
        // No-op; legacy Dashboard will retry, or the route will
        // render with a sensible empty state.
      }
    };

    if (providers.length === 0) safeFetch('/api/providers', setProviders);
    if (locations.length === 0) safeFetch('/api/locations', setLocations);
    if (cptCodes.length === 0) safeFetch('/api/cpt-codes', setCptCodes);
    if (cptBundles.length === 0) safeFetch('/api/cpt-bundles', setCptBundles);
    if (serviceTypes.length === 0)
      safeFetch('/api/treatment-planning/service-types', setServiceTypes);
    if (users.length === 0) safeFetch('/api/staff-list', setUsers);
    if (roles.length === 0) safeFetch('/api/roles', setRoles);
    // Jun 2026 — Bills/Eligibility billing-label dots need the label
    // catalog. Now routed through ClientsContext's self-healing
    // ensureClientLabelsLoaded (retry + window-focus refetch) instead
    // of a silent one-shot fetch.
    ensureClientLabelsLoaded();
    // Jun 2026 (user request) — the V2 appointment dialog needs POS
    // codes + organizations; without these, deep-linking into /v2/*
    // showed "Unknown" POS descriptions until the legacy Dashboard
    // mounted.
    if (posCodes.length === 0) safeFetch('/api/pos-codes', setPosCodes);
    if (organizations.length === 0) safeFetch('/api/organizations', setOrganizations);
    // Jun 2026 (user report) — the intake-bundle picker in the V2
    // appointment dialog "intermittently" didn't appear: intakeBundles
    // was only lazy-loaded by the LEGACY Dashboard (admin tab /
    // showAppointmentForm effect), so pure-V2 sessions had an empty
    // list until the user happened to visit a legacy surface.
    if (intakeBundles.length === 0) safeFetch('/api/intake-bundles', setIntakeBundles);
    // Jun 2026 (user report) — the V2 appointment dialog's group
    // selectors showed "No groups available" because clientGroups was
    // only fetched by the legacy Dashboard.
    if (clientGroups.length === 0) safeFetch('/api/client-groups', setClientGroups);
    // Jun 2026 — full client-list load is delegated to ClientsContext's
    // ensureClientsLoaded: it tracks completion via fullClientsLoadedRef
    // (a partial subset already in state no longer suppresses the
    // complete fields=minimal load), merges over partial rows, and
    // paginates past 1000 clients.
    ensureClientsLoaded();
    if (clients.length === 0) safeFetch('/api/clients?page_size=200&fields=minimal', setClients);

    setStaticDataLoaded(true);
    // We don't depend on the read values — only re-run when the
    // user identity changes.
  }, [user?.id]); // eslint-disable-line
};

export default useV2BootstrapData;
